import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowUpRight } from '@phosphor-icons/react';

const R = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 12 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: [0.22, 0.03, 0.26, 1] }} className={className}>{children}</motion.div>
  );
};

const SL = ({ children, light }) => <p className={`text-[11px] tracking-[0.2em] uppercase font-medium mb-4 ${light ? 'text-white/25' : 'text-slate-300'}`}>{children}</p>;
const H2 = ({ children, light, className = '' }) => <h2 className={`text-[1.6rem] sm:text-[2rem] md:text-[2.4rem] font-bold tracking-[-0.02em] leading-[1.1] ${light ? 'text-white' : 'text-[#0F172A]'} ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h2>;
const H3 = ({ children, className = '' }) => <h3 className={`text-base md:text-lg font-semibold text-[#0F172A] mb-3 ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>;
const P = ({ children, className = '' }) => <p className={`text-[13px] md:text-[14px] text-slate-400 leading-[1.75] ${className}`}>{children}</p>;
const Divider = () => <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12"><div className="border-t border-black/[0.04]" /></div>;

/* ── Architecture Diagram (SVG) ── */
const ArchDiagram = () => {
  const layers = [
    { label: 'Interface Layer', sub: 'React SPA · 26 views', color: '#e8eef5' },
    { label: 'Application Layer', sub: 'FastAPI · 17 route modules', color: '#f0f0f0' },
    { label: 'Intelligence Layer', sub: '7 specialised services', color: '#e5eeeb' },
    { label: 'Data Layer', sub: 'MongoDB · scoped isolation', color: '#eeecea' },
    { label: 'Background Processing', sub: 'Async task dispatch', color: '#f0ede5' },
    { label: 'Authentication', sub: 'JWT · RBAC · HTTP-only cookies', color: '#ecedf0' },
  ];
  const w = 440, rh = 52, gap = 10, pad = 24;
  const h = layers.length * (rh + gap) - gap + pad * 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full max-w-[380px]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {layers.map((l, i) => {
        const y = pad + i * (rh + gap);
        return (
          <g key={i}>
            <rect x={20} y={y} width={w - 40} height={rh} rx={8} fill={l.color} stroke="#d0d5dd" strokeWidth={0.5} />
            <text x={w / 2} y={y + 22} textAnchor="middle" fontSize={13} fontWeight={600} fill="#0F172A">{l.label}</text>
            <text x={w / 2} y={y + 38} textAnchor="middle" fontSize={10} fill="#94a3b8">{l.sub}</text>
            {i < layers.length - 1 && (
              <line x1={w / 2} y1={y + rh} x2={w / 2} y2={y + rh + gap} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" />
            )}
          </g>
        );
      })}
    </svg>
  );
};

/* ── Data Flow Diagram (SVG) ── */
const FlowDiagram = () => {
  const data = ['Conditions', 'Medications', 'Symptoms', 'Documents', 'Daily Logs', 'Events'];
  const intel = ['Health State', 'Safety Layer', 'Insights Engine'];
  const output = ['Chat Guidance', 'Daily Brain', 'Courses', 'Safety Signals'];
  const w = 700, h = 320, colW = 160, gap = 60;
  const x1 = 30, x2 = x1 + colW + gap, x3 = x2 + colW + gap;
  const rh = 32, rGap = 8;
  const col = (items, x, startY) => items.map((t, i) => ({ t, x, y: startY + i * (rh + rGap) }));
  const dItems = col(data, x1, 56);
  const iItems = col(intel, x2, 56 + 1.5 * (rh + rGap));
  const oItems = col(output, x3, 56 + 1 * (rh + rGap));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Column labels */}
      <text x={x1 + colW / 2} y={30} textAnchor="middle" fontSize={10} fontWeight={600} fill="#94a3b8" letterSpacing="0.15em">DATA</text>
      <text x={x2 + colW / 2} y={30} textAnchor="middle" fontSize={10} fontWeight={600} fill="#235347" letterSpacing="0.15em">INTELLIGENCE</text>
      <text x={x3 + colW / 2} y={30} textAnchor="middle" fontSize={10} fontWeight={600} fill="#94a3b8" letterSpacing="0.15em">OUTPUT</text>
      {/* Arrows: data -> intel */}
      {dItems.map((d, i) => iItems.map((t, j) => (
        <line key={`d${i}i${j}`} x1={d.x + colW} y1={d.y + rh / 2} x2={t.x} y2={t.y + rh / 2} stroke="#e2e8f0" strokeWidth={0.5} />
      )))}
      {/* Arrows: intel -> output */}
      {iItems.map((s, i) => oItems.map((t, j) => (
        <line key={`i${i}o${j}`} x1={s.x + colW} y1={s.y + rh / 2} x2={t.x} y2={t.y + rh / 2} stroke="#e2e8f0" strokeWidth={0.5} />
      )))}
      {/* Data boxes */}
      {dItems.map(({ t, x, y }, i) => (
        <g key={`d${i}`}><rect x={x} y={y} width={colW} height={rh} rx={6} fill="#f8f9fa" stroke="#e2e8f0" strokeWidth={0.5} /><text x={x + colW / 2} y={y + rh / 2 + 4} textAnchor="middle" fontSize={11} fill="#64748b">{t}</text></g>
      ))}
      {/* Intel boxes */}
      {iItems.map(({ t, x, y }, i) => (
        <g key={`i${i}`}><rect x={x} y={y} width={colW} height={rh} rx={6} fill="#e8f0ec" stroke="#235347" strokeWidth={0.5} strokeOpacity={0.3} /><text x={x + colW / 2} y={y + rh / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={600} fill="#235347">{t}</text></g>
      ))}
      {/* Output boxes */}
      {oItems.map(({ t, x, y }, i) => (
        <g key={`o${i}`}><rect x={x} y={y} width={colW} height={rh} rx={6} fill="#f8f9fa" stroke="#e2e8f0" strokeWidth={0.5} /><text x={x + colW / 2} y={y + rh / 2 + 4} textAnchor="middle" fontSize={11} fill="#64748b">{t}</text></g>
      ))}
    </svg>
  );
};

/* ── Metric Block ── */
const Metric = ({ value, label }) => (
  <div>
    <p className="text-lg md:text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">{label}</p>
  </div>
);


const TechnicalBriefPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white text-[#0F172A] overflow-x-hidden antialiased">

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-black/[0.04]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12 h-[60px] flex items-center justify-between">
          <button onClick={() => navigate('/')}>
            <img src="/kindra-logo.png" alt="Kindra" className="h-[22px] md:h-[26px] object-contain" />
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/presentation')} className="text-xs text-slate-400 hover:text-slate-600 transition-colors font-medium px-3 py-2 hidden sm:block">Product overview</button>
            <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Technical%20Inquiry" className="bg-[#0F172A] text-white text-[13px] px-5 py-[9px] rounded-lg font-medium hover:bg-slate-800 transition-colors">Get in touch</a>
          </div>
        </div>
      </nav>


      {/* ═══ 1. HERO ═══ */}
      <section className="pt-[120px] md:pt-[140px] pb-16 md:pb-20">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Technical brief</SL></R>
          <R delay={0.04}>
            <H2 className="max-w-2xl">A serious health platform, built for real-world use and long-term scale.</H2>
          </R>
          <R delay={0.08}>
            <P className="mt-5 max-w-lg">Kindra is a personal AI health intelligence system designed to support users continuously — not just during appointments — with architecture focused on responsiveness, reliability, privacy, and long-term scalability.</P>
          </R>
          <R delay={0.12}>
            <p className="mt-10 text-[11px] text-slate-300 tracking-[0.12em] uppercase">Live in production &middot; Real users &middot; Built for scale</p>
          </R>
        </div>
      </section>

      <Divider />

      {/* ═══ 2. EXECUTIVE SUMMARY ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Executive summary</SL></R>
          <R delay={0.04}>
            <p className="text-lg md:text-xl font-semibold text-[#0F172A] leading-[1.4] max-w-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Kindra is not a prototype or a wrapper around a language model.</p>
          </R>
          <R delay={0.08}>
            <P className="mt-4 max-w-xl">It is a structured, production system that ingests, connects, and reasons over a user's health data — including conditions, medications, symptoms, documents, appointments, and daily patterns — to provide personalised guidance and support over time.</P>
            <P className="mt-4 max-w-xl">This document outlines the system at a high level, focusing on architecture, performance design, scalability, data handling, and system defensibility.</P>
            <P className="mt-4 max-w-xl italic text-slate-300">Proprietary logic and internal orchestration are intentionally abstracted.</P>
          </R>
        </div>
      </section>

      <Divider />

      {/* ═══ 3. SYSTEM OVERVIEW ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>System overview</SL></R>
          <R delay={0.04}><H2 className="max-w-2xl">A modular health intelligence platform, not a single interface.</H2></R>
          <R delay={0.08}>
            <P className="mt-5 max-w-xl">Kindra operates across three core modes:</P>
            <div className="mt-8 grid md:grid-cols-3 gap-x-12 gap-y-6">
              {[
                ['Reactive', 'Answering user questions with full health context.'],
                ['Proactive', 'Generating daily insights, safety signals, and prompts.'],
                ['Structured', 'Building personalised learning and action pathways.'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.04 * i}>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <P>{d}</P>
                </R>
              ))}
            </div>
          </R>
          <R delay={0.14}>
            <P className="mt-10 max-w-xl">At its foundation, Kindra maintains a persistent, evolving health context per user. Every interaction enriches that context. The system becomes more valuable over time as it learns and connects more of the user's health reality.</P>
          </R>
        </div>
      </section>


      {/* ═══ 4. CORE ARCHITECTURE ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Core architecture</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Coordinated layers, not a monolith.</H2></R>
          <R delay={0.08}>
            <div className="mt-12 grid lg:grid-cols-[5fr_4fr] gap-12 lg:gap-20 items-start">
              <div className="space-y-8">
                {[
                  ['Interface Layer', 'React-based application handling all user interactions: onboarding, chat, health records, appointments, documents, courses, daily intelligence.'],
                  ['Application Layer', 'FastAPI backend providing structured endpoints across all domains: health data, chat, documents, appointments, courses, timeline, export and sharing.'],
                  ['Intelligence Layer', 'Seven specialised services responsible for reasoning and synthesis: Health Brain, Health State, Safety Layer, Insights Engine, University Service, Document Intelligence, AI Service.'],
                  ['Data Layer', 'User-scoped storage model ensuring strict data isolation, indexed access patterns, and efficient retrieval.'],
                  ['Background Processing', 'Non-blocking execution for health state recomputation, insight generation, fact extraction, and appointment preparation.'],
                  ['Authentication', 'JWT-based authentication with role-based access and secure token handling.'],
                ].map(([t, d], i) => (
                  <R key={i} delay={0.03 * i}>
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                    <P>{d}</P>
                  </R>
                ))}
              </div>
              <R delay={0.1}>
                <div className="sticky top-24">
                  <ArchDiagram />
                </div>
              </R>
            </div>
          </R>
        </div>
      </section>


      {/* ═══ 5. PRODUCT ARCHITECTURE ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Product architecture</SL></R>
          <R delay={0.04}><H2 className="max-w-2xl">A connected system, not isolated features.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-4">
              {['Structured health profile', 'Appointment management', 'Document intelligence', 'Contextual chat', 'Daily prioritisation', 'Personalised learning', 'Progress tracking', 'Safety monitoring'].map((t, i) => (
                <R key={i} delay={0.02 * i}>
                  <p className="text-[13px] text-slate-500 py-2 border-b border-slate-100">{t}</p>
                </R>
              ))}
            </div>
          </R>
          <R delay={0.14}>
            <P className="mt-8 max-w-lg font-medium text-slate-500">All features read from and contribute to the same canonical health context. There is a single source of truth, not separate systems per feature.</P>
          </R>
        </div>
      </section>

      <Divider />

      {/* ═══ 6. TECHNOLOGY STACK ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Technology stack</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Modern, production-ready, model-flexible.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-6">
              {[
                ['Frontend', 'React SPA'],
                ['Backend', 'Python FastAPI'],
                ['Database', 'MongoDB'],
                ['Deployment', 'Docker containers'],
                ['Serving', 'Nginx'],
                ['Intelligence', 'LLM via abstraction layer'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.02 * i}>
                  <p className="text-[11px] text-slate-300 tracking-wider uppercase">{t}</p>
                  <p className="text-sm text-[#0F172A] font-medium mt-0.5">{d}</p>
                </R>
              ))}
            </div>
          </R>
          <R delay={0.12}><P className="mt-8 max-w-lg">The system is designed to remain model-flexible and infrastructure-portable.</P></R>
        </div>
      </section>


      {/* ═══ 7. PERFORMANCE ═══ */}
      <section className="bg-[#0F172A] text-white py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL light>Performance & responsiveness</SL></R>
          <R delay={0.04}>
            <p className="text-lg md:text-xl font-semibold text-white/90 leading-[1.4] max-w-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>User actions should never wait for non-essential processing.</p>
          </R>

          <R delay={0.08}>
            <div className="mt-12 grid md:grid-cols-2 gap-16">
              <div>
                <p className="text-[11px] text-white/25 tracking-[0.2em] uppercase font-medium mb-5">Synchronous path</p>
                <div className="space-y-4">
                  {[['Database writes', 'Milliseconds'], ['Context assembly', '~5–10ms'], ['Chat streaming', 'First tokens in ~3 seconds'], ['API response delivery', 'Immediate']].map(([t, d], i) => (
                    <div key={i} className="flex justify-between items-baseline border-b border-white/[0.06] pb-3">
                      <p className="text-[13px] text-white/50">{t}</p>
                      <p className="text-[13px] text-white/80 font-medium">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-white/25 tracking-[0.2em] uppercase font-medium mb-5">Asynchronous path</p>
                <div className="space-y-4">
                  {['Health state recomputation', 'Safety signal generation', 'Fact extraction from chat', 'Appointment preparation', 'Insight refinement'].map((t, i) => (
                    <div key={i} className="flex items-baseline border-b border-white/[0.06] pb-3">
                      <p className="text-[13px] text-white/50">{t}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </R>

          <R delay={0.14}>
            <div className="mt-12 border-t border-white/[0.06] pt-8">
              <p className="text-[11px] text-white/25 tracking-[0.2em] uppercase font-medium mb-3">Streaming & responsiveness</p>
              <P className="text-white/40">Chat responses are streamed via server-sent events (SSE). Users see the first response tokens within seconds, with content rendering progressively as it is generated. Non-essential enrichment — health fact extraction, state recomputation, safety analysis — runs asynchronously after the primary response is delivered. The user is never blocked by background processing.</P>
            </div>
          </R>
        </div>
      </section>


      {/* ═══ DATA FLOW DIAGRAM ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Data flow</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">From structured data to personalised output.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 overflow-x-auto -mx-6 px-6 md:mx-0 md:px-0">
              <FlowDiagram />
            </div>
          </R>
          <R delay={0.12}><P className="mt-6 max-w-lg">Raw data is preserved. Derived intelligence is computed separately. All system outputs operate on this shared, evolving context layer.</P></R>
        </div>
      </section>

      <Divider />

      {/* ═══ 8. SCALABILITY ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Scalability</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Architected to scale without structural redesign.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid md:grid-cols-2 gap-x-16 gap-y-8">
              {[
                ['Key properties', 'Stateless backend services. Containerised deployment. User-isolated data model. Modular service boundaries.'],
                ['Data scaling', 'MongoDB supports horizontal scaling. User-scoped data enables clean partitioning.'],
                ['Compute scaling', 'AI calls can scale independently. Background processing can move to distributed queues.'],
                ['Architecture path', 'The system can evolve from single-node to distributed architecture without rework.'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.03 * i}>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <P>{d}</P>
                </R>
              ))}
            </div>
          </R>
        </div>
      </section>


      {/* ═══ 9. SECURITY & PRIVACY ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Security, privacy & protection</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Designed with healthcare-grade expectations.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid md:grid-cols-3 gap-16">
              <div>
                <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-5">Data isolation</p>
                <div className="space-y-3">
                  {['Strict per-user data scoping', 'No cross-user data access', 'Server-side context construction', 'No client-side prompt exposure'].map((t, i) => (
                    <p key={i} className="text-[13px] text-slate-500 pb-2 border-b border-slate-100">{t}</p>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-5">Platform protection</p>
                <div className="space-y-3">
                  {['Per-user rate limiting on AI endpoints', 'Global request safety limits', 'JWT authentication (HTTP-only cookies)', 'Encrypted transport'].map((t, i) => (
                    <p key={i} className="text-[13px] text-slate-500 pb-2 border-b border-slate-100">{t}</p>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-5">Data protection</p>
                <div className="space-y-3">
                  {['Automated daily backups', 'Off-site backup storage', '7-day backup retention', 'Documented restore process'].map((t, i) => (
                    <p key={i} className="text-[13px] text-slate-500 pb-2 border-b border-slate-100">{t}</p>
                  ))}
                </div>
              </div>
            </div>
          </R>
          <R delay={0.14}>
            <P className="mt-8 max-w-lg font-medium text-slate-500">Health data remains contained within the system and is not used for external model training. Usage is controlled and protected against abuse and runaway cost.</P>
          </R>
        </div>
      </section>


      {/* ═══ 10. DATA MODEL ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Data model</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">A structured, longitudinal health context.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-3">
              {['Conditions', 'Medications', 'Symptoms', 'Clinical events', 'Daily logs', 'Documents', 'Memory facts', 'Care team', 'Appointments', 'Health state'].map((t, i) => (
                <R key={i} delay={0.02 * i}>
                  <p className="text-[13px] text-[#0F172A] font-medium py-2 border-b border-slate-100">{t}</p>
                </R>
              ))}
            </div>
          </R>
        </div>
      </section>

      <Divider />

      {/* ═══ 11. AI STRATEGY ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>AI strategy</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">AI is an intelligence layer, not the system itself.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid md:grid-cols-2 gap-16">
              <div>
                <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-5">Roles of AI</p>
                <div className="space-y-3">
                  {['Contextual health guidance', 'Structured data extraction', 'Health state synthesis', 'Safety signal analysis', 'Insight generation', 'Course creation'].map((t, i) => (
                    <p key={i} className="text-[13px] text-slate-500 pb-2 border-b border-slate-100">{t}</p>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-5">Architectural stance</p>
                <P>AI is mediated by system logic. Outputs are structured, validated, and controlled. The system is not dependent on a single model design.</P>
                <P className="mt-4">This allows consistency, safety, and long-term adaptability.</P>
              </div>
            </div>
          </R>
        </div>
      </section>


      {/* ═══ 12. RELIABILITY ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Reliability & observability</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Built with production discipline.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid md:grid-cols-3 gap-x-12 gap-y-8">
              {[
                ['Reliability design', 'Graceful degradation across AI-dependent features. Fallback pathways for course generation. Idempotent operations where required.'],
                ['Observability', 'Request-level logging with latency tracking. Background task monitoring. Health state version tracking. Performance breakdowns per request.'],
                ['Data durability', 'Automated daily backups stored off-site with 7-day retention. Persistent volume storage for uploaded documents. Documented restore procedures.'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.03 * i}>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <P>{d}</P>
                </R>
              ))}
            </div>
          </R>
          <R delay={0.12}><P className="mt-8 max-w-lg font-medium text-slate-500">Failures in non-critical systems do not block user success. All user data is backed up daily and recoverable.</P></R>
        </div>
      </section>


      {/* ═══ 13. DEPLOYMENT ═══ */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL>Development & deployment</SL></R>
          <R delay={0.04}><H2 className="max-w-xl">Rapid iteration with controlled deployment.</H2></R>
          <R delay={0.08}>
            <div className="mt-10 grid md:grid-cols-3 gap-x-12 gap-y-6">
              {[
                ['Environment', 'Containerised (Docker). Deployed on dedicated infrastructure. Separated preview and production environments. Persistent volume storage for database and user files.'],
                ['Deployment', 'Automated build and restart process. Health checks and validation. Configuration via environment variables. Automated daily off-site backups.'],
                ['Philosophy', 'Fast iteration. Production stability. Controlled rollout.'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.03 * i}>
                  <h4 className="text-sm font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <P>{d}</P>
                </R>
              ))}
            </div>
          </R>
        </div>
      </section>


      {/* ═══ 14. DEFENSIBILITY ═══ */}
      <section className="bg-[#0F172A] text-white py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12">
          <R><SL light>Defensibility</SL></R>
          <R delay={0.04}>
            <p className="text-lg md:text-xl font-semibold text-white/90 leading-[1.4] max-w-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Kindra's defensibility is not based on features. It is based on system behaviour over time.</p>
          </R>
          <R delay={0.08}>
            <div className="mt-12 grid md:grid-cols-5 gap-x-8 gap-y-8">
              {[
                ['Longitudinal context', 'Health data accumulates and connects over months and years.'],
                ['Connected model', 'Conditions, medications, symptoms, and history are linked — not siloed.'],
                ['Compounding AI', 'Intelligence improves as the context deepens.'],
                ['Personalised output', 'Every response is shaped by the individual, not generic.'],
                ['Behavioural lock-in', 'Daily use creates reliance that new products cannot replicate.'],
              ].map(([t, d], i) => (
                <R key={i} delay={0.03 * i}>
                  <h4 className="text-[13px] font-semibold text-white/80 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <p className="text-[12px] text-white/30 leading-[1.7]">{d}</p>
                </R>
              ))}
            </div>
          </R>
          <R delay={0.16}>
            <P className="mt-12 text-white/40 max-w-lg">The more a user engages, the more context is built, the more accurate the system becomes, the harder it is to replace. This creates a data and behaviour moat, not a feature moat.</P>
          </R>
        </div>
      </section>


      {/* ═══ 15. FINAL CLOSE ═══ */}
      <section className="py-24 md:py-36">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 lg:px-12 text-center">
          <R>
            <H2 className="max-w-2xl mx-auto">A real platform with the foundations required for long-term scale.</H2>
          </R>
          <R delay={0.06}>
            <P className="mt-5 max-w-lg mx-auto">Kindra combines structured health intelligence, system-driven AI, and a scalable architecture designed to support real users over time.</P>
          </R>
          <R delay={0.12}>
            <div className="mt-10 flex flex-wrap justify-center items-center gap-3">
              <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Technical%20Discussion" className="bg-[#0F172A] text-white pl-6 pr-5 py-[13px] rounded-lg text-[13px] font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                Technical discussion <ArrowUpRight size={14} />
              </a>
              <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Investor%20Brief%20Request" className="text-slate-400 px-5 py-[13px] text-[13px] font-medium hover:text-slate-600 transition-colors">
                Request full brief
              </a>
            </div>
          </R>
          <R delay={0.18}>
            <div className="mt-20"><img src="/kindra-logo.png" alt="" className="h-5 mx-auto object-contain opacity-10" /></div>
          </R>
        </div>
      </section>
    </div>
  );
};

export default TechnicalBriefPage;
